/**
 * PartyKit WebSocket Service
 *
 * Manages WebSocket connections to PartyKit rooms for real-time multiplayer gameplay.
 * Provides connection management, message sending/receiving, automatic reconnection,
 * and connection status tracking.
 *
 * Features:
 * - Connection lifecycle management (connect, disconnect, reconnect)
 * - Automatic reconnection with exponential backoff
 * - Connection status tracking
 * - Message sending and receiving with JSON serialization
 * - Error handling and logging
 *
 * Phase 5.1: PartyKit Client Integration
 */

import PartySocket from 'partysocket';

/**
 * Connection status constants
 */
export const CONNECTION_STATUS = {
  DISCONNECTED: 'DISCONNECTED',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  RECONNECTING: 'RECONNECTING',
  ERROR: 'ERROR',
};

/**
 * Module state
 */
let socket = null;
let connectionStatus = CONNECTION_STATUS.DISCONNECTED;
let reconnectAttempts = 0;
let reconnectTimer = null;
let shouldReconnect = true;
let currentRoomId = null;
let currentHost = null;
let currentOnMessage = null;

/**
 * Configuration
 */
const MAX_RECONNECT_ATTEMPTS = 5;
const INITIAL_RECONNECT_DELAY = 1000; // 1 second
const MAX_RECONNECT_DELAY = 30000; // 30 seconds
const RECONNECT_BACKOFF_MULTIPLIER = 2;

/**
 * Calculate reconnection delay with exponential backoff
 */
function getReconnectDelay() {
  const delay = Math.min(
    INITIAL_RECONNECT_DELAY *
      Math.pow(RECONNECT_BACKOFF_MULTIPLIER, reconnectAttempts),
    MAX_RECONNECT_DELAY
  );
  return delay;
}

/**
 * Clear reconnection timer
 */
function clearReconnectTimer() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

/**
 * Handle socket open event
 */
function handleOpen() {
  console.log('[PartyService] WebSocket connection opened');
  connectionStatus = CONNECTION_STATUS.CONNECTED;
  reconnectAttempts = 0; // Reset reconnect attempts on successful connection
  clearReconnectTimer();
}

/**
 * Handle socket message event
 */
function handleMessage(event) {
  try {
    const data = JSON.parse(event.data);
    console.log('[PartyService] Message received:', data.type);

    if (currentOnMessage) {
      currentOnMessage(data);
    }
  } catch (error) {
    console.error('[PartyService] Failed to parse message:', error);
  }
}

/**
 * Handle socket close event
 */
function handleClose(event) {
  console.log('[PartyService] WebSocket connection closed', {
    code: event.code,
    reason: event.reason,
  });

  connectionStatus = CONNECTION_STATUS.DISCONNECTED;

  // Reconnect logic
  // Code 1000 is normal closure, don't reconnect
  // Code 1001-1015 are various close codes, reconnect for abnormal closures
  const shouldAttemptReconnect =
    shouldReconnect &&
    event.code !== 1000 &&
    reconnectAttempts < MAX_RECONNECT_ATTEMPTS;

  if (shouldAttemptReconnect) {
    connectionStatus = CONNECTION_STATUS.RECONNECTING;
    const delay = getReconnectDelay();

    console.log(
      `[PartyService] Attempting reconnection in ${delay}ms (attempt ${reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS})`
    );

    reconnectTimer = setTimeout(() => {
      reconnectAttempts++;
      attemptReconnection();
    }, delay);
  } else if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    console.error('[PartyService] Max reconnection attempts reached');
    connectionStatus = CONNECTION_STATUS.ERROR;
  }
}

/**
 * Handle socket error event
 */
function handleError(event) {
  console.error('[PartyService] WebSocket error:', event.error || event);
  // Don't change status here, wait for close event
}

/**
 * Attempt to reconnect to the room
 */
function attemptReconnection() {
  if (!currentRoomId || !currentHost || !currentOnMessage) {
    console.error('[PartyService] Cannot reconnect: missing connection info');
    return;
  }

  console.log('[PartyService] Reconnecting to room:', currentRoomId);

  // Create new connection with same parameters
  createPartyConnection(currentRoomId, currentHost, currentOnMessage);
}

/**
 * Create a new PartySocket connection
 *
 * @param {string} roomId - The PartyKit room ID to connect to
 * @param {string} host - The PartyKit host (e.g., 'localhost:1999' or 'myparty.username.partykit.dev')
 * @param {function} onMessage - Callback function for received messages
 * @returns {PartySocket} The created socket instance
 * @throws {Error} If required parameters are missing
 */
export function createPartyConnection(roomId, host, onMessage) {
  // Validate parameters
  if (!roomId || roomId.trim() === '') {
    throw new Error('Room ID is required');
  }

  if (!host || host.trim() === '') {
    throw new Error('Host is required');
  }

  if (!onMessage || typeof onMessage !== 'function') {
    throw new Error('onMessage callback is required');
  }

  // Don't create multiple connections to same room
  if (
    socket &&
    currentRoomId === roomId &&
    connectionStatus !== CONNECTION_STATUS.DISCONNECTED
  ) {
    console.log('[PartyService] Already connected to room:', roomId);
    return socket;
  }

  // Store connection info for reconnection
  currentRoomId = roomId;
  currentHost = host;
  currentOnMessage = onMessage;
  shouldReconnect = true;

  // Clean up existing connection
  if (socket) {
    socket.removeEventListener('open', handleOpen);
    socket.removeEventListener('message', handleMessage);
    socket.removeEventListener('close', handleClose);
    socket.removeEventListener('error', handleError);
    socket.close();
  }

  // Update status
  connectionStatus = CONNECTION_STATUS.CONNECTING;

  // Create new PartySocket instance
  console.log(
    '[PartyService] Creating connection to room:',
    roomId,
    'host:',
    host
  );

  socket = new PartySocket({
    host,
    room: roomId,
  });

  // Register event listeners
  socket.addEventListener('open', handleOpen);
  socket.addEventListener('message', handleMessage);
  socket.addEventListener('close', handleClose);
  socket.addEventListener('error', handleError);

  return socket;
}

/**
 * Send a message through the WebSocket connection
 *
 * @param {object} message - The message object to send (will be JSON stringified)
 * @throws {Error} If not connected or message is invalid
 */
export function sendMessage(message) {
  if (!socket || connectionStatus !== CONNECTION_STATUS.CONNECTED) {
    throw new Error('Not connected to party');
  }

  if (!message || typeof message !== 'object') {
    throw new Error('Message must be an object');
  }

  console.log('[PartyService] Sending message:', message.type);
  socket.send(JSON.stringify(message));
}

/**
 * Disconnect from the PartyKit room
 *
 * Closes the WebSocket connection and prevents automatic reconnection.
 */
export function disconnectParty() {
  console.log('[PartyService] Disconnecting from party');

  // Prevent reconnection
  shouldReconnect = false;
  clearReconnectTimer();

  // Clean up socket
  if (socket) {
    socket.removeEventListener('open', handleOpen);
    socket.removeEventListener('message', handleMessage);
    socket.removeEventListener('close', handleClose);
    socket.removeEventListener('error', handleError);

    socket.close();
    socket = null;
  }

  // Reset state
  connectionStatus = CONNECTION_STATUS.DISCONNECTED;
  currentRoomId = null;
  currentHost = null;
  currentOnMessage = null;
  reconnectAttempts = 0;
}

/**
 * Get the current connection status
 *
 * @returns {string} One of CONNECTION_STATUS values
 */
export function getConnectionStatus() {
  return connectionStatus;
}

/**
 * Check if currently connected
 *
 * @returns {boolean} True if connected
 */
export function isConnected() {
  return connectionStatus === CONNECTION_STATUS.CONNECTED;
}

/**
 * Get the current room ID
 *
 * @returns {string|null} The current room ID or null if not connected
 */
export function getCurrentRoomId() {
  return currentRoomId;
}

/**
 * Get the underlying PartySocket instance
 *
 * @returns {PartySocket|null} The socket instance or null if not connected
 */
export function getSocket() {
  return socket;
}
